import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { type ColumnDef, type RowSelectionState, type SortingState } from "@tanstack/react-table";
import { ExternalLink, Link2, MessageCircle, MoreHorizontal, Send, UserPlus } from "lucide-react";

import { UserAvatar } from "@/components/common/UserAvatar";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { DataTable } from "@/components/ui/data-table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import type { TikTokMarketplaceCreator } from "@/services/tiktokShop.service";
import { formatCompactCount } from "@/utils/formatters";

import { ageLabel, creatorPick, formatMoney, gmvSortValue } from "./creatorSearchFormat";

/** TikTok's cap on creators in one targeted invitation. */
const MAX_INVITE_CREATORS = 50;

const PROGRAM_INVITE_PENDING = "Waiting on TikTok confirming creators may be invited into your own program";


export function GmvCell({ creator }: { creator: TikTokMarketplaceCreator }) {
  if (creator.gmv) return <span className="tabular-nums text-foreground">{formatMoney(creator.gmv)}</span>;
  if (creator.gmvRange) {
    return (
      <span className="tabular-nums text-muted-foreground" title="Range only: the creator has not shared exact sales">
        {creator.gmvRange.label}
      </span>
    );
  }
  return <span className="text-muted-foreground">—</span>;
}

export function SignalChips({ creator, hideEmpty, max }: { creator: TikTokMarketplaceCreator; hideEmpty?: boolean; max?: number }) {
  const chips: Array<{ label: string; tone: "brand" | "good" | "muted"; title?: string }> = [];
  if (creator.affiliate) chips.push({ label: "Your affiliate", tone: "brand", title: creator.affiliate.name });
  if (creator.signals.collaborated) chips.push({ label: "Worked with you", tone: "good" });
  if (creator.signals.fastGrowing) chips.push({ label: "Fast growing", tone: "good" });
  if (creator.signals.risingStar) chips.push({ label: "Rising star", tone: "good" });
  if (creator.signals.invitedLast90Days) chips.push({ label: "Invited", tone: "muted", title: "Your shop invited this creator on TikTok in the last 90 days" });
  if (chips.length === 0) return hideEmpty ? null : <span className="text-xs text-muted-foreground">—</span>;
  const tones = {
    brand: "bg-brand-100 text-brand-700 dark:bg-brand-950 dark:text-brand-300",
    good: "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300",
    muted: "bg-muted text-muted-foreground",
  };
  return (
    <span className="flex flex-wrap gap-1">
      {chips.slice(0, max ?? chips.length).map((chip) => (
        <span key={chip.label} title={chip.title} className={`whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium ${tones[chip.tone]}`}>
          {chip.label}
        </span>
      ))}
      {max !== undefined && chips.length > max && (
        <span className="text-[11px] text-muted-foreground" title={chips.slice(max).map((c) => c.label).join(", ")}>
          +{chips.length - max}
        </span>
      )}
    </span>
  );
}

function RowMenu({ creator }: { creator: TikTokMarketplaceCreator }) {
  const navigate = useNavigate();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          aria-label={`Actions for @${creator.username}`}
          onClick={(e) => e.stopPropagation()}
        >
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64" onClick={(e) => e.stopPropagation()}>
        <DropdownMenuItem asChild>
          <a href={creator.profileUrl} target="_blank" rel="noreferrer">
            <ExternalLink className="h-4 w-4" />
            Open on TikTok
          </a>
        </DropdownMenuItem>
        {creator.affiliate && (
          <DropdownMenuItem disabled>
            <Link2 className="h-4 w-4" />
            Already your affiliate: {creator.affiliate.name}
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => navigate("/brand/tiktok?tab=invites", { state: { inviteCreators: [creatorPick(creator)] } })}>
          <Send className="h-4 w-4" />
          Send TikTok collab invite
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => navigate("/brand/tiktok?tab=messages", { state: { messageCreator: creatorPick(creator) } })}>
          <MessageCircle className="h-4 w-4" />
          Message on TikTok
        </DropdownMenuItem>
        <DropdownMenuItem disabled title={PROGRAM_INVITE_PENDING}>
          <UserPlus className="h-4 w-4" />
          Invite to your Reveshare program
        </DropdownMenuItem>
        <p className="px-2 pb-1.5 pt-1 text-[11px] leading-snug text-muted-foreground">{PROGRAM_INVITE_PENDING}.</p>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

interface Props {
  creators: TikTokMarketplaceCreator[];
  loading?: boolean;
  onOpen: (creator: TikTokMarketplaceCreator) => void;
}

/**
 * Marketplace results as a comparison table. Sorting is local to the loaded
 * pages; the default order is TikTok's own ranking.
 */
export function CreatorResultsTable({ creators, loading, onOpen }: Props) {
  const navigate = useNavigate();
  const [sorting, setSorting] = useState<SortingState>([]);
  const [selection, setSelection] = useState<RowSelectionState>({});
  // Keep only creators still on screen (a new search replaces the list).
  const selected = creators.filter((c) => selection[c.openId]);
  const atCap = selected.length >= MAX_INVITE_CREATORS;
  const toggle = (openId: string, on: boolean) =>
    setSelection((prev) => {
      const next = { ...prev };
      if (on && Object.keys(next).filter((k) => next[k]).length < MAX_INVITE_CREATORS) next[openId] = true;
      else delete next[openId];
      return next;
    });
  const inviteSelected = () =>
    navigate("/brand/tiktok?tab=invites", { state: { inviteCreators: selected.map(creatorPick) } });

  const columns = useMemo<ColumnDef<TikTokMarketplaceCreator>[]>(() => {
    const cols: ColumnDef<TikTokMarketplaceCreator>[] = [
      {
        id: "select",
        meta: { width: "2.5rem" },
        header: ({ table }) => {
          const rows = table.getRowModel().rows;
          const all = rows.length > 0 && rows.slice(0, MAX_INVITE_CREATORS).every((r) => r.getIsSelected());
          return (
            <Checkbox
              checked={all}
              aria-label="Select creators on this page"
              onCheckedChange={(v) => {
                const next: RowSelectionState = {};
                if (v === true) rows.slice(0, MAX_INVITE_CREATORS).forEach((r) => (next[r.id] = true));
                table.setRowSelection(next);
              }}
            />
          );
        },
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            aria-label={`Select @${row.original.username}`}
            onCheckedChange={(v) => row.toggleSelected(v === true)}
          />
        ),
      },
      {
        id: "creator",
        header: "Creator",
        meta: { width: "15rem" },
        cell: ({ row }) => {
          const c = row.original;
          return (
            <span className="flex min-w-0 items-center gap-2.5">
              <UserAvatar src={c.avatarUrl} name={c.nickname || c.username} className="h-9 w-9 shrink-0 text-xs" />
              <span className="flex min-w-0 flex-col">
                <a
                  href={c.profileUrl}
                  target="_blank"
                  rel="noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="max-w-[11rem] truncate font-medium text-foreground hover:underline"
                >
                  @{c.username}
                </a>
                <span className="flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground">
                  <span className="max-w-[8.5rem] truncate">{c.nickname ?? ""}</span>
                  {c.region && <span className="rounded bg-muted px-1 text-[10px] font-semibold">{c.region}</span>}
                </span>
              </span>
            </span>
          );
        },
      },
      {
        id: "niche",
        header: "Niche",
        cell: ({ row }) => {
          const named = row.original.categories.filter((c) => c.name);
          if (named.length === 0) return <span className="text-xs text-muted-foreground">—</span>;
          return (
            <span className="flex max-w-[11rem] items-baseline gap-1 text-sm text-foreground" title={named.map((c) => c.name).join(", ")}>
              <span className="truncate">{named[0].name}</span>
              {named.length > 1 && <span className="shrink-0 text-xs text-muted-foreground">+{named.length - 1}</span>}
            </span>
          );
        },
      },
      {
        id: "followers",
        header: "Followers",
        accessorFn: (c) => c.followers ?? -1,
        enableSorting: true,
        meta: { fit: true, headerClassName: "text-right", cellClassName: "text-right" },
        cell: ({ row }) => (
          <span className="tabular-nums">{row.original.followers === null ? "—" : formatCompactCount(row.original.followers)}</span>
        ),
      },
      {
        id: "gmv",
        header: () => <span title="A creator who keeps sales private shows a range; ranges sort by their lower end">GMV 30d</span>,
        accessorFn: gmvSortValue,
        enableSorting: true,
        meta: { fit: true, headerClassName: "text-right", cellClassName: "text-right" },
        cell: ({ row }) => <GmvCell creator={row.original} />,
      },
    ];
    cols.push(
      {
        id: "views",
        header: "Avg views",
        accessorFn: (c) => c.avgVideoViews ?? -1,
        enableSorting: true,
        meta: { fit: true, headerClassName: "text-right", cellClassName: "text-right" },
        cell: ({ row }) => {
          const c = row.original;
          return (
            <span className="flex flex-col items-end tabular-nums">
              <span>{c.avgVideoViews === null ? "—" : formatCompactCount(c.avgVideoViews)}</span>
              {Boolean(c.avgLiveViewers) && (
                <span className="text-[11px] text-muted-foreground">{formatCompactCount(c.avgLiveViewers ?? 0)} LIVE</span>
              )}
            </span>
          );
        },
      },
      {
        id: "audience",
        header: "Audience",
        meta: { fit: true },
        cell: ({ row }) => {
          const a = row.original.audience;
          if (!a.ageRanges.length && !a.gender) return <span className="text-muted-foreground">—</span>;
          return (
            <span className="flex flex-col whitespace-nowrap text-sm">
              <span className="text-foreground">{a.ageRanges[0] ? ageLabel(a.ageRanges[0]) : "—"}</span>
              {a.gender && a.genderPercent && (
                <span className="text-[11px] text-muted-foreground">
                  {a.genderPercent}% {a.gender === "FEMALE" ? "women" : "men"}
                </span>
              )}
            </span>
          );
        },
      },
      {
        id: "signals",
        header: "Signals",
        cell: ({ row }) => <SignalChips creator={row.original} max={1} />,
      },
      {
        id: "actions",
        header: () => <span className="sr-only">Actions</span>,
        meta: { fit: true },
        cell: ({ row }) => <RowMenu creator={row.original} />,
      },
    );
    return cols;
  }, []);

  return (
    <>
      {selected.length > 0 && (
        <div className="sticky top-0 z-10 flex flex-wrap items-center gap-3 rounded-lg border border-brand-200 bg-brand-50 px-3 py-2 text-sm dark:border-brand-900 dark:bg-brand-950">
          <span className="font-medium text-foreground">
            {selected.length} selected{atCap ? ` (TikTok allows ${MAX_INVITE_CREATORS} per invite)` : ""}
          </span>
          <span className="ml-auto flex gap-2">
            <Button type="button" variant="ghost" size="sm" onClick={() => setSelection({})}>
              Clear
            </Button>
            <Button type="button" size="sm" onClick={inviteSelected}>
              <Send className="h-3.5 w-3.5" />
              Send collab invite to {selected.length}
            </Button>
          </span>
        </div>
      )}
      <div className="hidden md:block">
        <DataTable
          columns={columns}
          data={creators}
          isLoading={loading}
          skeletonRows={8}
          emptyText="No creators match. Try removing a filter."
          onRowClick={(row) => onOpen(row)}
          getRowId={(row) => row.openId}
          sorting={sorting}
          onSortingChange={setSorting}
          rowSelection={selection}
          onRowSelectionChange={(updater) =>
            setSelection((prev) => {
              const next = typeof updater === "function" ? updater(prev) : updater;
              const ids = Object.keys(next).filter((k) => next[k]).slice(0, MAX_INVITE_CREATORS);
              return Object.fromEntries(ids.map((k) => [k, true]));
            })
          }
        />
      </div>

      {/* Phones: a card list with the numbers that decide a shortlist. */}
      <ul className="space-y-2 md:hidden">
        {creators.map((c) => (
          <li key={c.openId}>
            <div className="flex w-full items-center gap-3 rounded-xl border border-border bg-card p-3">
              <Checkbox
                checked={Boolean(selection[c.openId])}
                disabled={!selection[c.openId] && atCap}
                aria-label={`Select @${c.username}`}
                onCheckedChange={(v) => toggle(c.openId, v === true)}
              />
              <button type="button" onClick={() => onOpen(c)} className="flex min-w-0 flex-1 items-center gap-3 text-left">
                <UserAvatar src={c.avatarUrl} name={c.nickname || c.username} className="h-10 w-10 shrink-0 text-xs" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-medium text-foreground">@{c.username}</span>
                  <span className="flex gap-3 text-xs text-muted-foreground">
                    <span>{c.followers === null ? "—" : formatCompactCount(c.followers)} followers</span>
                    <GmvCell creator={c} />
                  </span>
                </span>
              </button>
              <RowMenu creator={c} />
            </div>
          </li>
        ))}
      </ul>
    </>
  );
}
